import {
  ENDPOINTS,
  ClauseCandidateDecisionRequestSchema,
  ClauseCandidateDetailRequestSchema,
  ClauseCandidateListRequestSchema,
  type ClauseCandidateDecisionRequest,
  type ClauseCandidateDecisionResponse,
  type ClauseCandidateDetailRequest,
  type ClauseCandidateDetailResponse,
  type ClauseCandidateListRequest,
  type ClauseCandidateListResponse,
} from '@clm/shared-types';

import { apiGet, apiPost, type Query } from './client';
import { substitutePath } from './verifications';

/**
 * `clause-candidates`, `clause-candidate-detail`, `clause-candidate-decision` —
 * the CLM-36 Legal review queue, wrapped for the browser (CLM-53).
 *
 * Same recipe as `verifications.ts`: the path comes from the registry, the
 * request is `.parse`d before it leaves the browser (every request schema is
 * `.strict()`, so an invented or scope-shaped key is a ZodError here rather
 * than a parameter on the wire), and the response is **parsed** rather than
 * asserted so an off-contract answer is a defect to report, not data to show.
 *
 * ## What the list is, and is not
 *
 * The clusters are corpus-level (FR-17 — all authorised may read the library
 * and what is proposed for it), so the list is *unscoped* and its `totals` are
 * corpus counts. The cluster **members** are paragraphs of specific contracts
 * and those are scoped server-side, anchored on `root_contract_key`; that is
 * why the detail response says `variants_in_scope`, never `variants`. Nothing
 * here narrows or widens that — every filter below is one the contract defines.
 *
 * ## The decision body carries the id twice, on purpose
 *
 * `cluster_id` goes in the path **and** in the body, as `verify-field` does:
 * `decode.ts` cross-checks the two and answers `validation_failed` on a
 * mismatch. The wrapper sends the id from the *validated body* in both places —
 * one source, no way to disagree.
 *
 * Encoding for the GET (`apps/api/src/http/decode.ts`): dotted keys for the
 * nested page object, a **repeated** key for `review_state[]`, booleans as the
 * literal strings `true`/`false`. `undefined` is omitted, never sent as `""`.
 */

const LIST_ENDPOINT = ENDPOINTS['clause-candidates'];
const DETAIL_ENDPOINT = ENDPOINTS['clause-candidate-detail'];
const DECISION_ENDPOINT = ENDPOINTS['clause-candidate-decision'];

/** Flatten a validated list request into the query the decoder expands. */
export function clauseCandidateListQuery(request: ClauseCandidateListRequest): Query {
  const query: Record<string, string | number | boolean | readonly string[]> = {};

  // A repeated key, not a comma-joined string (decode.ts).
  if (request.review_state !== undefined) query['review_state'] = [...request.review_state];
  if (request.mining_run_id !== undefined) query['mining_run_id'] = request.mining_run_id;
  if (request.described_only !== undefined) query['described_only'] = request.described_only;
  if (request.min_member_count !== undefined) {
    query['min_member_count'] = request.min_member_count;
  }
  if (request.page?.limit !== undefined) query['page.limit'] = request.page.limit;
  if (request.page?.cursor !== undefined) query['page.cursor'] = request.page.cursor;

  return query;
}

export async function fetchClauseCandidates(
  request: ClauseCandidateListRequest,
  signal?: AbortSignal,
): Promise<ClauseCandidateListResponse> {
  const validated = ClauseCandidateListRequestSchema.parse(request);
  const raw = await apiGet<unknown>(LIST_ENDPOINT.path, {
    query: clauseCandidateListQuery(validated),
    ...(signal === undefined ? {} : { signal }),
  });
  return LIST_ENDPOINT.response.parse(raw);
}

export async function fetchClauseCandidateDetail(
  request: ClauseCandidateDetailRequest,
  signal?: AbortSignal,
): Promise<ClauseCandidateDetailResponse> {
  const validated = ClauseCandidateDetailRequestSchema.parse(request);
  const path = substitutePath(DETAIL_ENDPOINT.path, { cluster_id: validated.cluster_id });
  const raw = await apiGet<unknown>(path, signal === undefined ? {} : { signal });
  return DETAIL_ENDPOINT.response.parse(raw);
}

/**
 * Record one of the four decisions. The **only** write this surface makes, and
 * the API answers it with the `clm.audit_log` receipt written in the same
 * transaction (NFR-07, rule 7) — the surface shows that receipt, never invents
 * one.
 */
export async function decideClauseCandidate(
  request: ClauseCandidateDecisionRequest,
  signal?: AbortSignal,
): Promise<ClauseCandidateDecisionResponse> {
  const body = ClauseCandidateDecisionRequestSchema.parse(request);
  const path = substitutePath(DECISION_ENDPOINT.path, { cluster_id: body.cluster_id });
  const raw = await apiPost<unknown>(path, body, signal === undefined ? {} : { signal });
  return DECISION_ENDPOINT.response.parse(raw);
}
